import type { RefObject } from 'react';

import { HelpCircleFillIcon, Text, Tooltip } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';

import type { TypeDeliveryAgencyOutboundUnit } from '~/types/delivery-agency.types';

import styles from './getDeliveryAgencyOutboundUnitColumnDefs.module.scss';

const CREATE_UNAVAILABLE_TOOLTIP = (
  <>
    {'해당 출고번호에 속한 모든 주문이\n'}
    <Text size="small" color="white" element="span" bold>
      주문취소 상태
    </Text>
    {'이거나 '}
    <Text size="small" color="white" element="span" bold>
      배송예정일이 과거
    </Text>
    {'인 경우\n추가 운송장 생성이 불가합니다.'}
  </>
);

const getDeliveryAgencyOutboundUnitColumnDefs = (tooltipPortalTargetRef: RefObject<HTMLElement | null>) => {
  const calculateTableHeight = (shippingLabelNumbers?: string[]) => {
    const LINE_HEIGHT = 20;
    const PADDING = 22;
    const HEADER_HEIGHT = 44;
    const EMPTY_OVERLAY_HEIGHT = 120;
    const contentHeight = shippingLabelNumbers?.length
      ? shippingLabelNumbers.length * LINE_HEIGHT + PADDING
      : EMPTY_OVERLAY_HEIGHT;
    return contentHeight + HEADER_HEIGHT;
  };

  const columnDefs: ColDef<TypeDeliveryAgencyOutboundUnit>[] = [
    {
      field: 'outboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'isCreatableShippingLabel',
      headerName: '추가 운송장 생성 가능 여부',
      width: 200,
      cellDataType: false,
      cellRenderer: ({ data }: ICellRendererParams<TypeDeliveryAgencyOutboundUnit>) => {
        if (!data) return '-';

        if (data.isCreatableShippingLabel) {
          return <span className={styles['creatable-text']}>생성 가능</span>;
        }

        return (
          <span className={styles['creatable-cell']}>
            <span className={styles['creatable-text']}>생성 불가능</span>
            <Tooltip
              description={CREATE_UNAVAILABLE_TOOLTIP}
              maxWidth="243px"
              portalTarget={tooltipPortalTargetRef.current}
            >
              <HelpCircleFillIcon size={16} color="#672091" />
            </Tooltip>
          </span>
        );
      },
    },
    {
      field: 'shippingLabelNumbers',
      headerName: '운송장 번호',
      width: 245,
      wrapText: true,
      autoHeight: true,
      cellStyle: { whiteSpace: 'pre', lineHeight: '20px', padding: '9px 0' },
      valueFormatter: ({
        value,
      }: ValueFormatterParams<
        TypeDeliveryAgencyOutboundUnit,
        TypeDeliveryAgencyOutboundUnit['shippingLabelNumbers']
      >) => value?.join('\n') ?? '-',
    },
    {
      field: 'receiverName',
      headerName: '수령자명',
      width: 100,
    },
    {
      field: 'productName',
      headerName: '상품명',
      flex: 1,
      minWidth: 252,
      width: 252,
    },
  ];

  return { calculateTableHeight, columnDefs };
};

export default getDeliveryAgencyOutboundUnitColumnDefs;
